import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import Modal from '../../components/shared/Modal';
import { useConfirm } from '../../components/shared/Confirm';
import { useIsMobile } from '../../utils/useIsMobile';

const POR_PAGINA = 10;

const TIPOS = {
  gasto_por_vencer:       { label: 'Gasto por vencer',       color: '#b45309', fondo: 'rgba(217,119,6,0.10)',  icono: '📌' },
  suscripcion_por_vencer: { label: 'Suscripción por vencer', color: '#2563eb', fondo: 'rgba(37,99,235,0.10)',  icono: '🔔' },
  suscripcion_vencida:    { label: 'Suscripción vencida',    color: '#c2410c', fondo: 'rgba(194,65,12,0.10)',  icono: '⚠️' },
  suscripcion_suspendida: { label: 'Servicio suspendido',    color: '#dc2626', fondo: 'rgba(220,38,38,0.10)',  icono: '🚫' },
};

const ENTREGADO = { label: 'Entregado',    color: '#16a34a', fondo: 'rgba(22,163,74,0.12)', icono: '✓' };
const PENDIENTE = { label: 'Pendiente',    color: '#d97706', fondo: 'rgba(217,119,6,0.12)', icono: '🕐' };
const FALLIDO   = { label: 'No entregado', color: '#dc2626', fondo: 'rgba(220,38,38,0.12)', icono: '✕' };

function estadoDe(m) {
  if (['fallido', 'invalido', 'omitido'].includes(m.estado)) return FALLIDO;
  if (['entregado', 'leido'].includes(m.estado_entrega)) {
    return m.estado_entrega === 'leido' ? { ...ENTREGADO, label: 'Leído' } : ENTREGADO;
  }
  return PENDIENTE;
}

// creado_en llega en UTC con sufijo "Z": el navegador lo pasa a hora local.
const fecha = (f) => { const d = new Date(f); return isNaN(d) ? '' : d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }); };
const hora  = (f) => { const d = new Date(f); return isNaN(d) ? '' : d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' }); };
const hoy      = () => new Date().toISOString().slice(0, 10);
const haceDias = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

// Misma sustitución que el servidor, para que la vista previa coincida.
function render(cuerpo, datos) {
  return String(cuerpo || '')
    .split('\n')
    .filter(l => {
      const ms = l.match(/\{\{(\w+)\}\}/g);
      if (!ms) return true;
      return ms.some(m => {
        const v = datos[m.slice(2, -2)];
        return v !== undefined && v !== null && String(v).trim() !== '';
      });
    })
    .join('\n')
    .replace(/\{\{(\w+)\}\}/g, (_, c) => (datos[c] == null ? '' : String(datos[c])))
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

const EJEMPLO = {
  negocio: 'Barbería & Estilo', plan: 'Plan Premium', valor: '$199.900',
  vence: '15/09/2026', cuando: 'en 5 días',
  descripcion: 'Hosting anual', monto: '$54.900',
  categoria: 'Servidores y hosting', proveedor: 'Hostinger',
};

/**
 * Contenido de Notificaciones de la plataforma. Va dentro de una pestaña de
 * Integraciones, asi que no trae encabezado de pagina propio: lo pone la
 * pantalla que lo contiene.
 */
export default function PlatformNotifications() {
  const [tab, setTab] = useState('historial');

  return (
    <>
      <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
        Avisos que envía la plataforma desde su número: gastos fijos por vencer a los
        super administradores, y estado de la suscripción a cada negocio.
      </p>

      {/* Sub-pestañas: en pildora, para no competir con las de arriba */}
      <div style={{ display: 'flex', gap: 6, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {[['historial', 'Historial'], ['plantillas', 'Mensajes']].map(([k, l]) => {
          const sel = tab === k;
          return (
            <button key={k} onClick={() => setTab(k)} style={{
              padding: '5px 14px', borderRadius: 999, cursor: 'pointer',
              fontSize: '0.82rem', fontFamily: 'var(--font-body)', fontWeight: sel ? 700 : 500,
              border: `1px solid ${sel ? 'var(--color-gold)' : 'var(--color-border)'}`,
              background: sel ? 'var(--color-gold-subtle)' : 'transparent',
              color: sel ? 'var(--color-gold)' : 'var(--color-text-muted)',
            }}>{l}</button>
          );
        })}
      </div>

      {tab === 'historial' ? <Historial /> : <Plantillas />}
    </>
  );
}

// ── Historial ───────────────────────────────────────────────────────────────

function Historial() {
  const esMovil = useIsMobile(1000);
  const [items, setItems]     = useState([]);
  const [resumen, setResumen] = useState(null);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [cargando, setCargando] = useState(true);
  const [error, setError]     = useState('');
  const [sel, setSel]         = useState(null);
  const [filtros, setFiltros] = useState({ buscar: '', tipo: '', desde: haceDias(30), hasta: hoy() });

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    const params = { ...filtros, page, limit: POR_PAGINA };
    Object.keys(params).forEach(k => { if (params[k] === '') delete params[k]; });
    try {
      const [lista, sum] = await Promise.all([
        api.get('/whatsapp/plataforma/messages', { params }),
        api.get('/whatsapp/plataforma/messages/summary', { params }),
      ]);
      setItems(lista.data.items || []);
      setTotal(lista.data.total || 0);
      setResumen(sum.data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo cargar el historial');
      setItems([]); setResumen(null); setTotal(0);
    } finally { setCargando(false); }
  }, [filtros, page]);

  useEffect(() => { cargar(); }, [cargar]);

  const cambiar = (campo, valor) => { setPage(1); setFiltros(f => ({ ...f, [campo]: valor })); };

  const paginas = Math.max(Math.ceil(total / POR_PAGINA), 1);
  const desde = total === 0 ? 0 : (page - 1) * POR_PAGINA + 1;
  const hasta = Math.min(page * POR_PAGINA, total);
  const pct = n => (resumen?.total ? Math.round((n / resumen.total) * 1000) / 10 : 0);

  return (
    <>
      {error && <div className="alert alert-danger" style={{ marginBottom: '1rem' }}>{error}</div>}

      {resumen && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: esMovil ? 'repeat(auto-fit, minmax(140px,1fr))' : 'repeat(4,1fr)',
          gap: '1rem', marginBottom: '1.5rem',
        }}>
          {[
            ['Entregadas', resumen.entregadas, '#16a34a', `${pct(resumen.entregadas)}% del total`],
            ['Pendientes', resumen.pendientes, '#d97706', `${pct(resumen.pendientes)}% del total`],
            ['No entregadas', resumen.no_entregadas, '#dc2626', `${pct(resumen.no_entregadas)}% del total`],
            ['Total enviadas', resumen.total, '#2563eb', 'En el periodo'],
          ].map(([t, v, c, p]) => (
            <div key={t} className="card" style={{ padding: '1rem 1.1rem' }}>
              <div style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>{t}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: c, lineHeight: 1.15 }}>{v}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{p}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: '0.85rem 1rem', marginBottom: '1.25rem' }}>
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
          <input className="form-input" style={{ margin: 0, flex: '1 1 200px' }}
                 placeholder="Buscar por teléfono, nombre o mensaje..."
                 value={filtros.buscar} onChange={e => cambiar('buscar', e.target.value)} />
          <input type="date" className="form-input" style={{ margin: 0, flex: '0 1 150px' }}
                 value={filtros.desde} onChange={e => cambiar('desde', e.target.value)} />
          <input type="date" className="form-input" style={{ margin: 0, flex: '0 1 150px' }}
                 value={filtros.hasta} onChange={e => cambiar('hasta', e.target.value)} />
          <select className="form-select" style={{ margin: 0, flex: '0 1 200px' }}
                  value={filtros.tipo} onChange={e => cambiar('tipo', e.target.value)}>
            <option value="">Todos los tipos</option>
            {Object.entries(TIPOS).map(([v, t]) => <option key={v} value={v}>{t.label}</option>)}
          </select>
        </div>
      </div>

      <div className="card" style={{ minWidth: 0 }}>
        {cargando ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
        ) : items.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
            La plataforma no ha enviado avisos en el periodo seleccionado
          </div>
        ) : (
          <div className="table-wrapper" style={{ border: 0 }}>
            <table style={{ width: '100%', minWidth: 720 }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}></th>
                  <th>Fecha y hora</th>
                  <th>Destinatario</th>
                  <th>Tipo</th>
                  <th>Estado entrega</th>
                  {!esMovil && <th>Mensaje</th>}
                </tr>
              </thead>
              <tbody>
                {items.map(m => {
                  const est = estadoDe(m);
                  const t = TIPOS[m.tipo] || { label: m.tipo, color: '#6b7280', fondo: 'var(--color-surface-3)', icono: '•' };
                  return (
                    <tr key={m.id} onClick={() => setSel(m)} style={{ cursor: 'pointer' }}>
                      <td>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 22, height: 22, borderRadius: '50%',
                          background: est.fondo, color: est.color, fontSize: '0.7rem', fontWeight: 700,
                        }}>{est.icono}</span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                        <div style={{ fontWeight: 600 }}>{fecha(m.creado_en)}</div>
                        <div style={{ color: 'var(--color-text-muted)' }}>{hora(m.creado_en)}</div>
                      </td>
                      <td style={{ fontSize: '0.82rem' }}>
                        {m.nombre_destinatario && <div style={{ fontWeight: 600 }}>{m.nombre_destinatario}</div>}
                        <div style={{ color: 'var(--color-text-muted)' }}>{m.telefono || '—'}</div>
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                          background: t.fondo, color: t.color, fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap',
                        }}>{t.icono} {t.label}</span>
                      </td>
                      <td>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                          background: est.fondo, color: est.color, fontSize: '0.7rem', fontWeight: 600,
                        }}>{est.label}</span>
                      </td>
                      {!esMovil && (
                        <td style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', maxWidth: 260 }}>
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {(m.cuerpo || '').replace(/[*_\n]/g, ' ').trim()}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
          padding: '0.9rem 1.1rem', borderTop: '1px solid var(--color-border)', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
            Mostrando {desde} a {hasta} de {total} resultados
          </span>
          {paginas > 1 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</button>
              <span style={{ fontSize: '0.8rem', alignSelf: 'center' }}>{page} / {paginas}</span>
              <button className="btn btn-ghost" disabled={page >= paginas} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          )}
        </div>
      </div>

      {sel && (
        <Modal titulo={(TIPOS[sel.tipo]?.label) || sel.tipo} ancho={460} onCerrar={() => setSel(null)}>
          {[
            ['Fecha', `${fecha(sel.creado_en)} ${hora(sel.creado_en)}`],
            ['Destinatario', sel.nombre_destinatario || '—'],
            ['Teléfono', sel.telefono || '—'],
            ['Negocio', sel.business_name || '—'],
            ['Estado', estadoDe(sel).label],
          ].map(([k, v]) => (
            <div key={k} style={{
              display: 'flex', justifyContent: 'space-between', gap: 12,
              padding: '6px 0', borderBottom: '1px solid var(--color-border)', fontSize: '0.84rem',
            }}>
              <span style={{ color: 'var(--color-text-muted)' }}>{k}</span>
              <span style={{ fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>{v}</span>
            </div>
          ))}

          {sel.error && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>⚠ Motivo del fallo</div>
              <div style={{
                background: 'rgba(220,38,38,0.08)', borderRadius: 8, padding: '0.7rem',
                fontSize: '0.78rem', lineHeight: 1.5, wordBreak: 'break-word',
              }}>{sel.error}</div>
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>💬 Mensaje</div>
            <div style={{
              background: 'var(--color-surface-3)', borderRadius: 8, padding: '0.85rem',
              fontSize: '0.82rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>{sel.cuerpo || '—'}</div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ── Plantillas ──────────────────────────────────────────────────────────────

function Plantillas() {
  const esMovil = useIsMobile(900);
  const [plantillas, setPlantillas] = useState([]);
  const [campos, setCampos] = useState({});
  const [activa, setActiva] = useState(null);
  const [texto, setTexto]   = useState('');
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso]   = useState('');
  const [error, setError]   = useState('');
  const areaRef = useRef(null);
  const confirmar = useConfirm();

  const notificar = (msg, esError = false) => {
    if (esError) { setError(msg); setTimeout(() => setError(''), 5000); }
    else         { setAviso(msg); setTimeout(() => setAviso(''), 4000); }
  };

  const cargar = useCallback(async () => {
    try {
      const r = await api.get('/whatsapp/plataforma/templates');
      setPlantillas(r.data.plantillas || []);
      setCampos(r.data.campos || {});
      setActiva(a => a || r.data.plantillas?.[0]?.tipo || null);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar los mensajes');
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const actual = plantillas.find(p => p.tipo === activa) || null;
  useEffect(() => { if (actual) setTexto(actual.texto); }, [actual?.tipo]); // eslint-disable-line
  const sinGuardar = actual && texto !== actual.texto;

  const elegir = async (tipo) => {
    if (sinGuardar && !await confirmar({
      titulo: 'Cambios sin guardar',
      mensaje: 'Tienes cambios sin guardar en este mensaje.',
      detalle: 'Si continúas se perderán.',
      aceptar: 'Descartar cambios',
      cancelar: 'Seguir editando',
      peligro: true,
    })) return;
    setActiva(tipo);
  };

  const insertar = (clave) => {
    const marcador = `{{${clave}}}`;
    const area = areaRef.current;
    if (!area) { setTexto(t => t + marcador); return; }
    const ini = area.selectionStart ?? texto.length;
    const fin = area.selectionEnd ?? texto.length;
    setTexto(texto.slice(0, ini) + marcador + texto.slice(fin));
    requestAnimationFrame(() => {
      area.focus();
      area.setSelectionRange(ini + marcador.length, ini + marcador.length);
    });
  };

  const guardar = async (restaurar = false) => {
    if (!actual) return;
    if (!restaurar && !texto.trim()) {
      return notificar('El mensaje no puede quedar vacío. Usa «Restaurar original».', true);
    }
    if (restaurar && !await confirmar({
      titulo: 'Restaurar mensaje original',
      mensaje: 'Se volverá al mensaje original de la plataforma.',
      detalle: 'Se perderá el texto que hayas personalizado.',
      aceptar: 'Restaurar',
      peligro: true,
    })) return;

    setGuardando(true);
    try {
      const r = await api.put('/whatsapp/plataforma/templates', {
        tipo: actual.tipo, cuerpo: restaurar ? '' : texto,
      });
      setPlantillas(ps => ps.map(p => p.tipo === actual.tipo
        ? { ...p, texto: r.data.texto, personalizada: r.data.personalizada } : p));
      setTexto(r.data.texto);
      notificar(r.data.message);
    } catch (err) {
      notificar(err.response?.data?.error || 'No se pudo guardar el mensaje', true);
    } finally { setGuardando(false); }
  };

  if (cargando) {
    return <div className="card" style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando mensajes...</div>;
  }

  return (
    <>
      {aviso && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{aviso}</div>}
      {error && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: '1rem' }}>
        {plantillas.map(p => {
          const sel = p.tipo === activa;
          return (
            <button key={p.tipo} onClick={() => elegir(p.tipo)} style={{
              padding: '7px 13px', borderRadius: 8, cursor: 'pointer', fontSize: '0.83rem',
              fontFamily: 'var(--font-body)', fontWeight: sel ? 700 : 500,
              border: `1px solid ${sel ? 'var(--color-gold)' : 'var(--color-border)'}`,
              background: sel ? 'var(--color-gold-subtle)' : 'transparent',
              color: sel ? 'var(--color-gold)' : 'var(--color-text-muted)',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              {p.titulo}
              {p.personalizada && <span title="Personalizado" style={{ fontSize: '0.7rem' }}>✎</span>}
            </button>
          );
        })}
      </div>

      {actual && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: esMovil ? '1fr' : 'minmax(0,1fr) 280px',
          gap: '1.25rem', alignItems: 'start',
        }}>
          <div style={{ minWidth: 0 }}>
            <div className="card" style={{ marginBottom: '1rem' }}>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{actual.titulo}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  {actual.cuando} Va dirigido a: <strong>{actual.destinatario}</strong>.
                </div>
              </div>

              <textarea ref={areaRef} className="form-input" rows={11} value={texto}
                        onChange={e => setTexto(e.target.value)}
                        style={{ fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical' }} />

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
                <button className="btn btn-primary" onClick={() => guardar(false)} disabled={guardando || !sinGuardar}>
                  {guardando ? 'Guardando...' : 'Guardar mensaje'}
                </button>
                {actual.personalizada && (
                  <button className="btn btn-ghost" onClick={() => guardar(true)} disabled={guardando}>
                    ↺ Restaurar original
                  </button>
                )}
                {sinGuardar && <span style={{ fontSize: '0.78rem', color: 'var(--color-warning)' }}>Sin guardar</span>}
              </div>
            </div>

            <div className="card">
              <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>Vista previa</div>
              <div style={{
                background: 'var(--color-surface-3)', borderRadius: 10, padding: '0.9rem 1rem',
                whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                fontSize: '0.85rem', lineHeight: 1.6, maxWidth: 420,
              }}>{render(texto, EJEMPLO) || <span style={{ color: 'var(--color-text-muted)' }}>El mensaje está vacío</span>}</div>
            </div>
          </div>

          <div className="card" style={{ alignSelf: 'start' }}>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 4 }}>Campos disponibles</div>
            <p style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)', margin: '0 0 12px' }}>
              Pulsa uno para insertarlo. Solo estos funcionan en este mensaje.
            </p>
            {actual.campos.map(clave => {
              const c = campos[clave] || { etiqueta: `{{${clave}}}`, descripcion: '' };
              return (
                <button key={clave} onClick={() => insertar(clave)} style={{
                  display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                  background: 'none', border: 'none', borderBottom: '1px solid var(--color-border)',
                  padding: '8px 2px', fontFamily: 'var(--font-body)',
                }}>
                  <code style={{ fontSize: '0.76rem', color: 'var(--color-gold)', fontWeight: 600 }}>{c.etiqueta}</code>
                  <div style={{ fontSize: '0.74rem', color: 'var(--color-text-muted)', lineHeight: 1.45 }}>
                    {c.descripcion}
                  </div>
                </button>
              );
            })}
            <p style={{ fontSize: '0.73rem', color: 'var(--color-text-muted)', margin: '12px 0 0', lineHeight: 1.5 }}>
              Si un campo llega vacío, esa línea del mensaje no se envía.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
